import React, { useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { uploadFile } from "@/lib/upload";

/** Drag & drop + click uploader. Returns uploaded media `{url, id, type}` via onUploaded. */
export default function FileUploader({ accept = "image/*", multiple = false, onUploaded, label = "Drop or click to upload", testid = "file-uploader" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);

  const handle = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true); setError(""); setProgress(0);
    try {
      const arr = Array.from(files);
      for (const f of arr) {
        const res = await uploadFile(f, (p) => setProgress(p));
        onUploaded?.(res);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false); setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      <label
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handle(e.dataTransfer.files); }}
        className={`flex flex-col items-center justify-center gap-2 border border-dashed cursor-pointer transition-colors p-6 ${dragging ? "border-[#C9A55A] bg-[#1a1a1a]" : "border-[#2A2A2A] hover:border-[#C9A55A]"}`}>
        <input ref={inputRef} type="file" accept={accept} multiple={multiple}
          className="hidden" onChange={(e) => handle(e.target.files)} data-testid={`${testid}-input`} />
        {busy ? (
          <>
            <Loader2 className="text-[#C9A55A] animate-spin" size={18} />
            <span className="text-xs text-[#C9A55A] tracking-[0.18em] uppercase">Uploading {progress}%</span>
          </>
        ) : (
          <>
            <Upload className="text-[#C9A55A]" size={18} />
            <span className="text-xs text-[#F5F1EB] tracking-[0.18em] uppercase">{label}</span>
          </>
        )}
      </label>
      {error && <p className="text-xs text-[#B8342A] mt-2">{error}</p>}
    </div>
  );
}
